'use client';

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useRouter } from 'next/navigation';
import { ArrowRight, ArrowLeft, Check, Eye, EyeOff, Loader2, Scissors, MapPin, Star, Zap, Shield, Play, Info, Upload, Users, TrendingUp, BookOpen, Lock, CheckCircle2, Sparkles,  } from 'lucide-react';
import { createClient } from '@/lib/supabase/client';

// ── Constants ──────────────────────────────────────────────────────────────
const GOLD = '#C9A84C';
const GOLD_RGB = '201,168,76';
const NAVY = '#0a0e1a';
const DARK = '#0d1220';

// ── Types ──────────────────────────────────────────────────────────────────
interface ProfileData {
  fullName: string;
  shopName: string;
  location: string;
  locationConfirmed: boolean;
  instagram: string;
  tiktok: string;
  facebook: string;
  yearsInBusiness: string;
  profilePic: File | null;
  profilePicUrl: string;
}

interface SurveyData {
  bookingSystem: string;
  bookingSystemOther: string;
  weeklyClients: number;
  biggestChallenges: string[];
  ageGroups: string[];
  genderGroups: string[];
  incomeGroups: string[];
  peakHours: Record<string, string[]>;
  planInterest: string;
}

interface AccountData {
  email: string;
  password: string;
  showPassword: boolean;
}

// ── Helpers ────────────────────────────────────────────────────────────────
function cn(...classes: (string | undefined | false | null)[]) {
  return classes.filter(Boolean).join(' ');
}

// ── Progress Bar ───────────────────────────────────────────────────────────
function ProgressBar({ pct, label }: { pct: number; label?: string }) {
  return (
    <div className="mb-6">
      {label && (
        <div className="flex justify-between items-center mb-2">
          <span className="text-xs font-semibold tracking-widest uppercase" style={{ color: 'rgba(255,255,255,0.4)' }}>
            {label}
          </span>
          <span className="text-xs font-bold tabular-nums" style={{ color: GOLD }}>
            {Math.round(pct)}%
          </span>
        </div>
      )}
      <div className="h-1.5 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.07)' }}>
        <div
          className="h-full rounded-full transition-all duration-700 ease-out"
          style={{
            width: `${pct}%`,
            background: `linear-gradient(90deg, ${GOLD}, rgba(201,168,76,0.65))`,
            boxShadow: `0 0 10px rgba(${GOLD_RGB},0.45)`,
          }}
        />
      </div>
    </div>
  );
}

// ── Gold Input ─────────────────────────────────────────────────────────────
function GoldInput({
  type = 'text',
  placeholder,
  value,
  onChange,
  error,
  showToggle,
  onToggle,
  show,
  icon,
  disabled,
}: {
  type?: string;
  placeholder: string;
  value: string;
  onChange: (v: string) => void;
  error?: string;
  showToggle?: boolean;
  onToggle?: () => void;
  show?: boolean;
  icon?: React.ReactNode;
  disabled?: boolean;
}) {
  const [focused, setFocused] = useState(false);
  return (
    <div>
      <div className="relative">
        {icon && (
          <div
            className="absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none"
            style={{ color: focused ? GOLD : 'rgba(255,255,255,0.3)' }}
          >
            {icon}
          </div>
        )}
        <input
          type={showToggle ? (show ? 'text' : 'password') : type}
          placeholder={placeholder}
          value={value}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
          onFocus={(e) => {
            setFocused(true);
            e.target.style.borderColor = GOLD;
            e.target.style.boxShadow = `0 0 0 3px rgba(${GOLD_RGB},0.12)`;
          }}
          onBlur={(e) => {
            setFocused(false);
            e.target.style.borderColor = error ? '#f87171' : 'rgba(255,255,255,0.1)';
            e.target.style.boxShadow = 'none';
          }}
          style={{
            width: '100%',
            padding: icon ? '12px 14px 12px 42px' : '12px 14px',
            paddingRight: showToggle ? '44px' : '14px',
            borderRadius: '12px',
            background: disabled ? 'rgba(255,255,255,0.03)' : 'rgba(255,255,255,0.05)',
            border: `1px solid ${error ? '#f87171' : 'rgba(255,255,255,0.1)'}`,
            color: 'white',
            fontSize: '14px',
            outline: 'none',
            transition: 'border-color 0.2s, box-shadow 0.2s',
          }}
        />
        {showToggle && (
          <button
            type="button"
            onClick={onToggle}
            className="absolute right-3.5 top-1/2 -translate-y-1/2 transition-colors"
            style={{ color: GOLD }}
          >
            {show ? <EyeOff size={16} /> : <Eye size={16} />}
          </button>
        )}
      </div>
      {error && <p className="text-xs text-red-400 mt-1.5 ml-1">{error}</p>}
    </div>
  );
}

// ── Tooltip ────────────────────────────────────────────────────────────────
function Tooltip({ text, children }: { text: string; children: React.ReactNode }) {
  const [visible, setVisible] = useState(false);
  return (
    <div className="relative inline-flex items-center">
      <div
        onMouseEnter={() => setVisible(true)}
        onMouseLeave={() => setVisible(false)}
        className="cursor-help"
      >
        {children}
      </div>
      {visible && (
        <div
          className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 z-50 w-52 rounded-xl p-3 text-xs leading-relaxed pointer-events-none"
          style={{
            background: 'rgba(20,25,40,0.97)',
            border: `1px solid rgba(${GOLD_RGB},0.3)`,
            color: 'rgba(255,255,255,0.85)',
            boxShadow: `0 8px 32px rgba(0,0,0,0.5), 0 0 0 1px rgba(${GOLD_RGB},0.1)`,
          }}
        >
          {text}
          <div
            className="absolute top-full left-1/2 -translate-x-1/2 w-0 h-0"
            style={{
              borderLeft: '6px solid transparent',
              borderRight: '6px solid transparent',
              borderTop: `6px solid rgba(${GOLD_RGB},0.3)`,
            }}
          />
        </div>
      )}
    </div>
  );
}

// ── Confetti ───────────────────────────────────────────────────────────────
function Confetti({ active }: { active: boolean }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animRef = useRef<number>(0);

  useEffect(() => {
    if (!active || !canvasRef.current) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    const colors = [GOLD, '#fff', '#f59e0b', '#fbbf24', '#fde68a', '#C9A84C'];
    const particles: {
      x: number; y: number; vx: number; vy: number;
      color: string; size: number; angle: number; spin: number;
    }[] = [];

    for (let i = 0; i < 160; i++) {
      particles.push({
        x: Math.random() * canvas.width,
        y: -20,
        vx: (Math.random() - 0.5) * 4,
        vy: Math.random() * 3 + 2,
        color: colors[Math.floor(Math.random() * colors.length)],
        size: Math.random() * 8 + 4,
        angle: Math.random() * Math.PI * 2,
        spin: (Math.random() - 0.5) * 0.2,
      });
    }

    let frame = 0;
    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      particles.forEach((p) => {
        p.x += p.vx;
        p.y += p.vy;
        p.vy += 0.05;
        p.angle += p.spin;
        ctx.save();
        ctx.translate(p.x, p.y);
        ctx.rotate(p.angle);
        ctx.fillStyle = p.color;
        ctx.globalAlpha = Math.max(0, 1 - frame / 180);
        ctx.fillRect(-p.size / 2, -p.size / 2, p.size, p.size / 2);
        ctx.restore();
      });
      frame++;
      if (frame < 200) {
        animRef.current = requestAnimationFrame(animate);
      } else {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
      }
    };
    animRef.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animRef.current);
  }, [active]);

  if (!active) return null;
  return (
    <canvas
      ref={canvasRef}
      className="fixed inset-0 pointer-events-none z-50"
    />
  );
}

// ── Screen 0: Welcome ──────────────────────────────────────────────────────
function Screen0({ onNext }: { onNext: () => void }) {
  const [chairsFilled, setChairsFilled] = useState(0);
  const [mounted, setMounted] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [videoProgress, setVideoProgress] = useState(0);
  const [videoTime, setVideoTime] = useState(0);
  const [captionIndex, setCaptionIndex] = useState(0);
  const [videoEnded, setVideoEnded] = useState(false);
  const [hasStarted, setHasStarted] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const VIDEO_DURATION = 30; // seconds

  const captions = [
    { time: 0,  text: "Hey! I\'m Mags — your AI barber coach." },
    { time: 4,  text: "You\'re about to set up your personal client pipeline." },
    { time: 8,  text: "This takes about 3 minutes — no fluff, I promise." },
    { time: 12, text: "First, we'll build your barber profile so clients know you." },
    { time: 16, text: "Then we\'ll learn about your shop and your goals." },
    { time: 20, text: "We\'ll match you with the right plan to fill your chairs." },
    { time: 24, text: "And finally, lock in your account so you're protected." },
    { time: 27, text: "Ready? Let\'s get every chair booked. Let\'s go! 🔥" },
  ];

  useEffect(() => {
    setMounted(true);
    const t = setInterval(() => {
      setChairsFilled((p) => (p < 5 ? p + 1 : p));
    }, 600);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (isPlaying) {
      intervalRef.current = setInterval(() => {
        setVideoTime((prev) => {
          const next = prev + 0.1;
          if (next >= VIDEO_DURATION) {
            setIsPlaying(false);
            setVideoEnded(true);
            if (intervalRef.current) clearInterval(intervalRef.current);
            return VIDEO_DURATION;
          }
          setVideoProgress((next / VIDEO_DURATION) * 100);
          // Update caption
          const activeCaption = [...captions].reverse().find((c) => next >= c.time);
          if (activeCaption) {
            const idx = captions.indexOf(activeCaption);
            setCaptionIndex(idx);
          }
          return next;
        });
      }, 100);
    } else {
      if (intervalRef.current) clearInterval(intervalRef.current);
    }
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [isPlaying]);

  const handlePlayPause = () => {
    if (videoEnded) {
      setVideoTime(0);
      setVideoProgress(0);
      setCaptionIndex(0);
      setVideoEnded(false);
      setIsPlaying(true);
      setHasStarted(true);
    } else {
      setIsPlaying((p) => !p);
      setHasStarted(true);
    }
  };

  const handleSeek = (e: React.MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const pct = (e.clientX - rect.left) / rect.width;
    const newTime = pct * VIDEO_DURATION;
    setVideoTime(newTime);
    setVideoProgress(pct * 100);
    const activeCaption = [...captions].reverse().find((c) => newTime >= c.time);
    if (activeCaption) setCaptionIndex(captions.indexOf(activeCaption));
    setVideoEnded(false);
  };

  const formatTime = (s: number) => {
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${m}:${sec.toString().padStart(2, '0')}`;
  };

  const chairs = [
    { delay: '0ms', label: 'Mon' },
    { delay: '600ms', label: 'Tue' },
    { delay: '1200ms', label: 'Wed' },
    { delay: '1800ms', label: 'Thu' },
    { delay: '2400ms', label: 'Fri' },
  ];

  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center relative overflow-hidden px-6 py-10"
      style={{ background: `radial-gradient(ellipse at 50% 0%, rgba(${GOLD_RGB},0.12) 0%, ${NAVY} 60%)` }}
    >
      {/* Animated background grid */}
      <div
        className="absolute inset-0 pointer-events-none opacity-[0.04]"
        style={{
          backgroundImage: `linear-gradient(rgba(${GOLD_RGB},0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(${GOLD_RGB},0.5) 1px, transparent 1px)`,
          backgroundSize: '48px 48px',
        }}
      />

      {/* Floating orbs */}
      <div
        className="absolute top-1/4 left-1/4 w-64 h-64 rounded-full pointer-events-none"
        style={{
          background: `radial-gradient(circle, rgba(${GOLD_RGB},0.08) 0%, transparent 70%)`,
          animation: 'float 6s ease-in-out infinite',
        }}
      />
      <div
        className="absolute bottom-1/4 right-1/4 w-48 h-48 rounded-full pointer-events-none"
        style={{
          background: `radial-gradient(circle, rgba(${GOLD_RGB},0.06) 0%, transparent 70%)`,
          animation: 'float 8s ease-in-out infinite reverse',
        }}
      />

      <div
        className="relative z-10 text-center max-w-lg w-full"
        style={{
          opacity: mounted ? 1 : 0,
          transform: mounted ? 'translateY(0)' : 'translateY(24px)',
          transition: 'opacity 0.7s ease, transform 0.7s ease',
        }}
      >
        {/* Logo mark */}
        <div className="flex justify-center mb-6">
          <div
            className="w-16 h-16 rounded-2xl flex items-center justify-center"
            style={{
              background: `rgba(${GOLD_RGB},0.12)`,
              border: `1px solid rgba(${GOLD_RGB},0.35)`,
              boxShadow: `0 0 40px rgba(${GOLD_RGB},0.2)`,
            }}
          >
            <Scissors size={28} style={{ color: GOLD }} />
          </div>
        </div>

        {/* Badge */}
        <div
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-bold mb-4"
          style={{
            background: `rgba(${GOLD_RGB},0.12)`,
            border: `1px solid rgba(${GOLD_RGB},0.3)`,
            color: GOLD,
          }}
        >
          <Sparkles size={12} />
          <span>The Barber Sphere — Get Ahead Program</span>
        </div>

        <h1
          className="text-4xl font-black text-white leading-[1.05] tracking-tight mb-3"
          style={{ textShadow: `0 0 40px rgba(${GOLD_RGB},0.3)` }}
        >
          Ready to Fill<br />
          <span style={{ color: GOLD }}>Every Chair?</span>
        </h1>
        <p className="text-sm leading-relaxed mb-6" style={{ color: 'rgba(255,255,255,0.55)' }}>
          Meet Mags — your AI barber coach. She'll walk you through the setup in 30 seconds.
        </p>

        {/* ── Mags Video Player ── */}
        <div
          className="relative w-full rounded-2xl overflow-hidden mb-6"
          style={{
            border: `1px solid rgba(${GOLD_RGB},0.3)`,
            boxShadow: `0 0 40px rgba(${GOLD_RGB},0.15), 0 20px 60px rgba(0,0,0,0.5)`,
            background: '#060a14',
          }}
        >
          {/* Video frame — 16:9 */}
          <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
            <div className="absolute inset-0">
              {/* Mags persona image */}
              <img
                src="/assets/images/mags_ai_persona.png"
                alt="Mags — AI Barber Coach"
                className="w-full h-full object-cover"
                style={{
                  filter: isPlaying ? 'brightness(0.85)' : 'brightness(0.7)',
                  transition: 'filter 0.4s ease',
                }}
              />

              {/* Dark overlay gradient */}
              <div
                className="absolute inset-0"
                style={{
                  background: 'linear-gradient(to top, rgba(6,10,20,0.95) 0%, rgba(6,10,20,0.2) 50%, transparent 100%)',
                }}
              />

              {/* Gold scan line animation when playing */}
              {isPlaying && (
                <div
                  className="absolute left-0 right-0 h-px pointer-events-none"
                  style={{
                    background: `linear-gradient(90deg, transparent, rgba(${GOLD_RGB},0.6), transparent)`,
                    top: `${videoProgress}%`,
                    transition: 'top 0.1s linear',
                    boxShadow: `0 0 8px rgba(${GOLD_RGB},0.4)`,
                  }}
                />
              )}

              {/* LIVE badge */}
              <div className="absolute top-3 left-3 flex items-center gap-2">
                <div
                  className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold tracking-widest uppercase"
                  style={{ background: 'rgba(0,0,0,0.6)', border: `1px solid rgba(${GOLD_RGB},0.4)`, color: GOLD }}
                >
                  <div
                    className="w-1.5 h-1.5 rounded-full"
                    style={{
                      background: GOLD,
                      animation: isPlaying ? 'pulse 1s ease-in-out infinite' : 'none',
                    }}
                  />
                  AI · Mags
                </div>
              </div>

              {/* Duration badge */}
              <div className="absolute top-3 right-3">
                <div
                  className="px-2.5 py-1 rounded-full text-[10px] font-bold"
                  style={{ background: 'rgba(0,0,0,0.6)', border: '1px solid rgba(255,255,255,0.12)', color: 'rgba(255,255,255,0.7)' }}
                >
                  0:30
                </div>
              </div>

              {/* Center play/pause button */}
              {!isPlaying && (
                <button
                  onClick={handlePlayPause}
                  className="absolute inset-0 flex items-center justify-center group"
                  aria-label={videoEnded ? 'Replay video' : hasStarted ? 'Resume video' : 'Play Mags introduction video'}
                >
                  <div
                    className="w-16 h-16 rounded-full flex items-center justify-center transition-all duration-200 group-hover:scale-110"
                    style={{
                      background: `rgba(${GOLD_RGB},0.2)`,
                      border: `2px solid rgba(${GOLD_RGB},0.6)`,
                      boxShadow: `0 0 30px rgba(${GOLD_RGB},0.4)`,
                      backdropFilter: 'blur(4px)',
                    }}
                  >
                    {videoEnded ? (
                      <svg width="22" height="22" viewBox="0 0 24 24" fill={GOLD}>
                        <path d="M12 5V1L7 6l5 5V7c3.31 0 6 2.69 6 6s-2.69 6-6 6-6-2.69-6-6H4c0 4.42 3.58 8 8 8s8-3.58 8-8-3.58-8-8-8z"/>
                      </svg>
                    ) : (
                      <Play size={22} style={{ color: GOLD, marginLeft: 3 }} fill={GOLD} />
                    )}
                  </div>
                </button>
              )}

              {/* Pause overlay (tap to pause while playing) */}
              {isPlaying && (
                <button
                  onClick={handlePlayPause}
                  className="absolute inset-0"
                  aria-label="Pause video"
                  style={{ background: 'transparent' }}
                />
              )}

              {/* Caption bar */}
              {hasStarted && (
                <div
                  className="absolute bottom-14 left-3 right-3 text-center"
                  style={{
                    opacity: isPlaying || videoEnded ? 1 : 0.7,
                    transition: 'opacity 0.3s ease',
                  }}
                >
                  <div
                    className="inline-block px-4 py-2 rounded-xl text-sm font-semibold text-white"
                    style={{
                      background: 'rgba(0,0,0,0.72)',
                      backdropFilter: 'blur(6px)',
                      border: '1px solid rgba(255,255,255,0.1)',
                      maxWidth: '90%',
                    }}
                  >
                    {videoEnded ? '✅ Setup takes just 3 minutes. Let\'s go!' : captions[captionIndex]?.text}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Video controls bar */}
          <div
            className="px-4 py-3 flex flex-col gap-2"
            style={{ background: 'rgba(6,10,20,0.97)', borderTop: `1px solid rgba(${GOLD_RGB},0.12)` }}
          >
            {/* Progress bar (seekable) */}
            <div
              className="relative h-1.5 rounded-full cursor-pointer group"
              style={{ background: 'rgba(255,255,255,0.1)' }}
              onClick={handleSeek}
            >
              <div
                className="absolute left-0 top-0 h-full rounded-full transition-all duration-100"
                style={{
                  width: `${videoProgress}%`,
                  background: `linear-gradient(90deg, ${GOLD}, rgba(${GOLD_RGB},0.7))`,
                  boxShadow: `0 0 6px rgba(${GOLD_RGB},0.5)`,
                }}
              />
              {/* Scrubber dot */}
              <div
                className="absolute top-1/2 -translate-y-1/2 w-3 h-3 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                style={{
                  left: `calc(${videoProgress}% - 6px)`,
                  background: GOLD,
                  boxShadow: `0 0 8px rgba(${GOLD_RGB},0.8)`,
                }}
              />
            </div>

            {/* Controls row */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {/* Play/Pause button */}
                <button
                  onClick={handlePlayPause}
                  className="w-7 h-7 rounded-full flex items-center justify-center transition-all hover:scale-110"
                  style={{ background: `rgba(${GOLD_RGB},0.15)`, border: `1px solid rgba(${GOLD_RGB},0.3)` }}
                  aria-label={isPlaying ? 'Pause' : 'Play'}
                >
                  {isPlaying ? (
                    <svg width="10" height="12" viewBox="0 0 10 12" fill={GOLD}>
                      <rect x="0" y="0" width="3" height="12" rx="1"/>
                      <rect x="7" y="0" width="3" height="12" rx="1"/>
                    </svg>
                  ) : (
                    <Play size={10} style={{ color: GOLD, marginLeft: 1 }} fill={GOLD} />
                  )}
                </button>

                {/* Time */}
                <span className="text-xs font-mono tabular-nums" style={{ color: 'rgba(255,255,255,0.5)' }}>
                  {formatTime(videoTime)} / 0:30
                </span>
              </div>

              {/* Mags label */}
              <div className="flex items-center gap-1.5">
                <Sparkles size={10} style={{ color: GOLD }} />
                <span className="text-xs font-semibold" style={{ color: `rgba(${GOLD_RGB},0.8)` }}>
                  Mags · AI Coach
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Animated barber chairs */}
        <div className="flex justify-center gap-3 mb-8">
          {chairs.map((chair, i) => (
            <div key={i} className="flex flex-col items-center gap-2">
              <div
                className="w-10 h-12 rounded-xl flex items-center justify-center transition-all duration-500"
                style={{
                  background: i < chairsFilled
                    ? `rgba(${GOLD_RGB},0.2)`
                    : 'rgba(255,255,255,0.04)',
                  border: `2px solid ${i < chairsFilled ? `rgba(${GOLD_RGB},0.5)` : 'rgba(255,255,255,0.08)'}`,
                  boxShadow: i < chairsFilled ? `0 0 16px rgba(${GOLD_RGB},0.25)` : 'none',
                  transform: i < chairsFilled ? 'scale(1.05)' : 'scale(1)',
                }}
              >
                <Scissors
                  size={18}
                  style={{
                    color: i < chairsFilled ? GOLD : 'rgba(255,255,255,0.2)',
                    transition: 'color 0.4s ease',
                  }}
                />
              </div>
              <span
                className="text-[10px] font-semibold tracking-wide"
                style={{ color: i < chairsFilled ? `rgba(${GOLD_RGB},0.8)` : 'rgba(255,255,255,0.2)' }}
              >
                {chair.label}
              </span>
            </div>
          ))}
        </div>

        {/* CTA */}
        <button
          onClick={onNext}
          className="w-full py-4 rounded-2xl text-base font-black tracking-wide transition-all duration-200 hover:scale-[1.02] active:scale-[0.98] flex items-center justify-center gap-3"
          style={{
            background: `linear-gradient(135deg, ${GOLD}, #a8832a)`,
            color: NAVY,
            boxShadow: `0 8px 32px rgba(${GOLD_RGB},0.4), 0 0 0 1px rgba(${GOLD_RGB},0.3)`,
          }}
        >
          <Zap size={18} />
          Let's Get Started
          <ArrowRight size={18} />
        </button>

        <p className="text-xs mt-4" style={{ color: 'rgba(255,255,255,0.25)' }}>
          Takes about 3 minutes · No credit card required
        </p>
      </div>

      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-20px); }
        }
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.3; }
        }
      `}</style>
    </div>
  );
}

// ── Screen 1: Profile Creation ─────────────────────────────────────────────
function Screen1({
  data,
  onChange,
  onNext,
  onBack,
}: {
  data: ProfileData;
  onChange: (d: Partial<ProfileData>) => void;
  onNext: () => void;
  onBack: () => void;
}) {
  const [detectingLocation, setDetectingLocation] = useState(false);
  const [locationError, setLocationError] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const fileRef = useRef<HTMLInputElement>(null);

  // Field completion progress
  const fields = [
    !!data.fullName,
    !!data.shopName,
    !!data.location && data.locationConfirmed,
    !!data.yearsInBusiness,
    !!data.profilePicUrl,
    !!(data.instagram || data.tiktok || data.facebook),
  ];
  const fieldPct = (fields.filter(Boolean).length / fields.length) * 100;

  const detectLocation = () => {
    setDetectingLocation(true);
    setLocationError('');
    if (!navigator.geolocation) {
      setLocationError('Geolocation not supported');
      setDetectingLocation(false);
      return;
    }
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          const res = await fetch(
            `https://nominatim.openstreetmap.org/reverse?lat=${pos.coords.latitude}&lon=${pos.coords.longitude}&format=json`
          );
          const json = await res.json();
          const city = json.address?.city || json.address?.town || json.address?.village || '';
          const state = json.address?.state || '';
          const loc = [city, state].filter(Boolean).join(', ');
          onChange({ location: loc, locationConfirmed: false });
        } catch {
          onChange({ location: `${pos.coords.latitude.toFixed(2)}, ${pos.coords.longitude.toFixed(2)}`, locationConfirmed: false });
        }
        setDetectingLocation(false);
      },
      () => {
        setLocationError('Could not detect location. Enter manually.');
        setDetectingLocation(false);
      }
    );
  };

  const handlePicUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const url = URL.createObjectURL(file);
    onChange({ profilePic: file, profilePicUrl: url });
  };

  const validate = () => {
    const errs: Record<string, string> = {};
    if (!data.fullName.trim()) errs.fullName = 'Name is required';
    if (!data.shopName.trim()) errs.shopName = 'Shop name is required';
    if (!data.location.trim()) errs.location = 'Location is required';
    if (!data.yearsInBusiness) errs.yearsInBusiness = 'Select your experience level';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleNext = () => {
    if (validate()) onNext();
  };

  const experienceLevels = [
    { value: 'rookie', label: 'Rookie', sub: '0–2 years', icon: '🌱' },
    { value: 'rising', label: 'Rising Star', sub: '3–7 years', icon: '⭐' },
    { value: 'master', label: 'Master Barber', sub: '8+ years', icon: '👑' },
  ];

  return (
    <div className="w-full max-w-[480px] mx-auto">
      <ProgressBar pct={fieldPct} label="Profile Completion" />

      <div className="mb-8">
        <h2 className="text-2xl font-black text-white mb-1">Build Your Profile</h2>
        <p className="text-sm" style={{ color: 'rgba(255,255,255,0.45)' }}>
          This is how clients and the platform will know you.
        </p>
      </div>

      <div className="space-y-5">
        {/* Profile pic */}
        <div className="flex items-center gap-4">
          <div
            className="w-16 h-16 rounded-2xl flex items-center justify-center overflow-hidden cursor-pointer transition-all hover:scale-105"
            style={{
              background: data.profilePicUrl ? 'transparent' : `rgba(${GOLD_RGB},0.08)`,
              border: `2px dashed ${data.profilePicUrl ? 'transparent' : `rgba(${GOLD_RGB},0.3)`}`,
            }}
            onClick={() => fileRef.current?.click()}
          >
            {data.profilePicUrl ? (
              <img src={data.profilePicUrl} alt="Profile preview" className="w-full h-full object-cover" />
            ) : (
              <Upload size={20} style={{ color: `rgba(${GOLD_RGB},0.6)` }} />
            )}
          </div>
          <div>
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              className="text-sm font-semibold transition-opacity hover:opacity-80"
              style={{ color: GOLD }}
            >
              {data.profilePicUrl ? 'Change photo' : 'Upload profile photo'}
            </button>
            <p className="text-xs mt-0.5" style={{ color: 'rgba(255,255,255,0.3)' }}>
              Optional · JPG, PNG up to 5MB
            </p>
          </div>
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handlePicUpload} />
        </div>

        {/* Name */}
        <div>
          <label className="block text-xs font-semibold mb-2 tracking-wide uppercase" style={{ color: 'rgba(255,255,255,0.45)' }}>
            Your Name *
          </label>
          <GoldInput
            placeholder="Marcus Johnson"
            value={data.fullName}
            onChange={(v) => onChange({ fullName: v })}
            error={errors.fullName}
          />
        </div>

        {/* Shop name */}
        <div>
          <label className="block text-xs font-semibold mb-2 tracking-wide uppercase" style={{ color: 'rgba(255,255,255,0.45)' }}>
            Shop Name *
          </label>
          <GoldInput
            placeholder="Marcus's Fade House"
            value={data.shopName}
            onChange={(v) => onChange({ shopName: v })}
            error={errors.shopName}
          />
        </div>

        {/* Location */}
        <div>
          <label className="block text-xs font-semibold mb-2 tracking-wide uppercase" style={{ color: 'rgba(255,255,255,0.45)' }}>
            Location *
          </label>
          <div className="flex gap-2">
            <div className="flex-1">
              <GoldInput
                placeholder="Atlanta, GA"
                value={data.location}
                onChange={(v) => onChange({ location: v, locationConfirmed: false })}
                error={errors.location}
                icon={<MapPin size={15} />}
              />
            </div>
            <button
              type="button"
              onClick={detectLocation}
              disabled={detectingLocation}
              className="px-3 py-2 rounded-xl text-xs font-semibold transition-all hover:opacity-90 flex items-center gap-1.5 shrink-0"
              style={{
                background: `rgba(${GOLD_RGB},0.12)`,
                border: `1px solid rgba(${GOLD_RGB},0.3)`,
                color: GOLD,
              }}
            >
              {detectingLocation ? <Loader2 size={13} className="animate-spin" /> : <MapPin size={13} />}
              {detectingLocation ? 'Detecting...' : 'Auto-detect'}
            </button>
          </div>
          {locationError && <p className="text-xs text-red-400 mt-1.5 ml-1">{locationError}</p>}
          {data.location && !data.locationConfirmed && (
            <div
              className="mt-2 flex items-center gap-2 px-3 py-2 rounded-xl"
              style={{ background: `rgba(${GOLD_RGB},0.08)`, border: `1px solid rgba(${GOLD_RGB},0.2)` }}
            >
              <span className="text-xs" style={{ color: 'rgba(255,255,255,0.7)' }}>
                Confirm: <strong style={{ color: 'white' }}>{data.location}</strong>
              </span>
              <button
                type="button"
                onClick={() => onChange({ locationConfirmed: true })}
                className="text-xs font-bold px-2 py-1 rounded-lg transition-colors"
                style={{ background: `rgba(${GOLD_RGB},0.2)`, color: GOLD }}
              >
                ✓ Confirm
              </button>
            </div>
          )}
          {data.locationConfirmed && (
            <p className="text-xs mt-1.5 ml-1 flex items-center gap-1" style={{ color: '#4ade80' }}>
              <CheckCircle2 size={12} /> Location confirmed
            </p>
          )}
        </div>

        {/* Social links */}
        <div>
          <div className="flex items-center gap-2 mb-2">
            <label className="text-xs font-semibold tracking-wide uppercase" style={{ color: 'rgba(255,255,255,0.45)' }}>
              Social Links
            </label>
            <Tooltip text="Adding social links helps clients find and trust you. Barbers with social profiles get 40% more bookings.">
              <Info size={13} style={{ color: `rgba(${GOLD_RGB},0.6)` }} />
            </Tooltip>
            <span className="text-xs" style={{ color: 'rgba(255,255,255,0.25)' }}>Optional</span>
          </div>
          <div className="space-y-2">
            <GoldInput
              placeholder="Instagram handle (@yourname)"
              value={data.instagram}
              onChange={(v) => onChange({ instagram: v })}
            />
            <GoldInput
              placeholder="TikTok handle (@yourname)"
              value={data.tiktok}
              onChange={(v) => onChange({ tiktok: v })}
            />
            <GoldInput
              placeholder="Facebook page URL"
              value={data.facebook}
              onChange={(v) => onChange({ facebook: v })}
            />
          </div>
        </div>

        {/* Years in business */}
        <div>
          <label className="block text-xs font-semibold mb-3 tracking-wide uppercase" style={{ color: 'rgba(255,255,255,0.45)' }}>
            Years in Business *
          </label>
          <div className="grid grid-cols-3 gap-3">
            {experienceLevels.map((lvl) => (
              <button
                key={lvl.value}
                type="button"
                onClick={() => onChange({ yearsInBusiness: lvl.value })}
                className="flex flex-col items-center gap-2 p-4 rounded-2xl transition-all duration-200 hover:scale-[1.02]"
                style={{
                  background: data.yearsInBusiness === lvl.value
                    ? `rgba(${GOLD_RGB},0.15)`
                    : 'rgba(255,255,255,0.04)',
                  border: `2px solid ${data.yearsInBusiness === lvl.value ? GOLD : 'rgba(255,255,255,0.08)'}`,
                  boxShadow: data.yearsInBusiness === lvl.value ? `0 0 20px rgba(${GOLD_RGB},0.2)` : 'none',
                }}
              >
                <span className="text-2xl">{lvl.icon}</span>
                <span className="text-xs font-bold text-white">{lvl.label}</span>
                <span className="text-[10px]" style={{ color: 'rgba(255,255,255,0.4)' }}>{lvl.sub}</span>
              </button>
            ))}
          </div>
          {errors.yearsInBusiness && (
            <p className="text-xs text-red-400 mt-2 ml-1">{errors.yearsInBusiness}</p>
          )}
        </div>
      </div>

      <div className="flex gap-3 mt-8">
        <button
          type="button"
          onClick={onBack}
          className="flex items-center gap-2 px-5 py-3 rounded-xl text-sm font-semibold transition-all hover:opacity-80"
          style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.6)' }}
        >
          <ArrowLeft size={16} /> Back
        </button>
        <button
          type="button"
          onClick={handleNext}
          className="flex-1 py-3 rounded-xl text-sm font-black tracking-wide transition-all duration-200 hover:scale-[1.01] active:scale-[0.99] flex items-center justify-center gap-2"
          style={{
            background: `linear-gradient(135deg, ${GOLD}, #a8832a)`,
            color: NAVY,
            boxShadow: `0 4px 20px rgba(${GOLD_RGB},0.35)`,
          }}
        >
          Continue <ArrowRight size={16} />
        </button>
      </div>
    </div>
  );
}

// ── Screen 2a: Booking & Challenges ───────────────────────────────────────
function Screen2a({
  data,
  onChange,
  onNext,
  onBack,
}: {
  data: SurveyData;
  onChange: (d: Partial<SurveyData>) => void;
  onNext: () => void;
  onBack: () => void;
}) {
  const [errors, setErrors] = useState<Record<string, string>>({});

  const bookingSystems = [
    { value: 'none', label: 'None / Pen & Paper', icon: '📝', tooltip: "Yep… old school. Don't worry, we can automate this for you." },
    { value: 'google', label: 'Google Calendar', icon: '📅', tooltip: 'Great for small shops, but we can help you capture leads faster.' },
    { value: 'square', label: 'Square Appointments', icon: '🟦', tooltip: 'Popular, but missing instant lead integration. We can fix that.' },
    { value: 'fresha', label: 'Fresha / Shedul', icon: '🌿', tooltip: "Good for stylists, but let's make it more revenue-focused." },
    { value: 'vagaro', label: 'Vagaro / Booker', icon: '💼', tooltip: "Nice tools, but we'll supercharge your appointments and leads." },
    { value: 'other', label: 'Custom / Other Software', icon: '🔧', tooltip: "Using something unique? No problem — we\'ll show you how to plug it in." },
    { value: 'manual', label: 'I manage manually with text / calls', icon: '📱', tooltip: "Manual hustle? Let\'s automate your client flow and save time." },
  ];

  const challenges = [
    { value: 'empty_chairs', label: 'Empty Chairs', icon: '💺' },
    { value: 'marketing', label: 'Marketing', icon: '📣' },
    { value: 'retention', label: 'Client Retention', icon: '🔄' },
    { value: 'scheduling', label: 'Scheduling', icon: '🗓️' },
  ];

  const toggleChallenge = (v: string) => {
    const cur = data.biggestChallenges;
    onChange({ biggestChallenges: cur.includes(v) ? cur.filter((c) => c !== v) : [...cur, v] });
  };

  const validate = () => {
    const errs: Record<string, string> = {};
    if (!data.bookingSystem) errs.bookingSystem = 'Select your current booking system';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  return (
    <div className="w-full max-w-[480px] mx-auto">
      <ProgressBar pct={40} label="Step 1 of 4 — Your Shop" />
      <div className="mb-8">
        <h2 className="text-2xl font-black text-white mb-1">How Do You Run Your Shop?</h2>
        <p className="text-sm" style={{ color: 'rgba(255,255,255,0.45)' }}>Tell us about your booking setup and biggest hurdles.</p>
      </div>
      <div className="space-y-7">
        {/* Booking system */}
        <div>
          <label className="block text-xs font-semibold mb-1 tracking-wide uppercase" style={{ color: 'rgba(255,255,255,0.45)' }}>
            What do you currently use to manage your bookings? *
          </label>
          <p className="text-xs mb-3 font-medium" style={{ color: `rgba(${GOLD_RGB},0.7)` }}>
            ✨ Choose your system and unlock your personalized tips →
          </p>
          <div className="flex flex-col gap-2">
            {bookingSystems.map((sys) => {
              const isSelected = data.bookingSystem === sys.value;
              return (
                <div key={sys.value} className="relative group">
                  <button
                    type="button"
                    onClick={() => onChange({ bookingSystem: sys.value })}
                    className="w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 text-left"
                    style={{
                      background: isSelected ? `rgba(${GOLD_RGB},0.15)` : 'rgba(255,255,255,0.04)',
                      border: `1.5px solid ${isSelected ? GOLD : 'rgba(255,255,255,0.08)'}`,
                      boxShadow: isSelected ? `0 0 12px rgba(${GOLD_RGB},0.15)` : 'none',
                    }}
                  >
                    <span className="text-xl flex-shrink-0">{sys.icon}</span>
                    <span className="text-sm font-semibold flex-1" style={{ color: isSelected ? GOLD : 'rgba(255,255,255,0.75)' }}>
                      {sys.label}
                    </span>
                    {isSelected && <Check size={14} style={{ color: GOLD, flexShrink: 0 }} />}
                    <div
                      className="flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center cursor-help"
                      style={{ background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.12)' }}
                      title={sys.tooltip}
                    >
                      <Info size={10} style={{ color: 'rgba(255,255,255,0.4)' }} />
                    </div>
                  </button>
                  <div
                    className="absolute right-0 top-full mt-1 z-50 w-64 rounded-xl p-3 text-xs leading-relaxed pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                    style={{
                      background: 'rgba(20,25,40,0.97)',
                      border: `1px solid rgba(${GOLD_RGB},0.3)`,
                      color: 'rgba(255,255,255,0.85)',
                      boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
                    }}
                  >
                    {sys.tooltip}
                  </div>
                </div>
              );
            })}
          </div>
          {data.bookingSystem === 'other' && (
            <div className="mt-2">
              <GoldInput
                placeholder="Which system do you use?"
                value={data.bookingSystemOther}
                onChange={(v) => onChange({ bookingSystemOther: v })}
              />
            </div>
          )}
          {errors.bookingSystem && <p className="text-xs text-red-400 mt-1.5 ml-1">{errors.bookingSystem}</p>}
          {(data.bookingSystem === 'none' || data.bookingSystem === 'manual') && (
            <div
              className="mt-3 px-4 py-3 rounded-xl text-xs font-medium leading-relaxed"
              style={{ background: `rgba(${GOLD_RGB},0.08)`, border: `1px solid rgba(${GOLD_RGB},0.25)`, color: `rgba(${GOLD_RGB},0.9)` }}
            >
              🎉 Good news: Get Ahead automates your bookings so you can focus on cutting hair, not chasing clients.
            </div>
          )}
        </div>

        {/* Weekly clients slider */}
        <div>
          <div className="flex justify-between items-center mb-3">
            <label className="text-xs font-semibold tracking-wide uppercase" style={{ color: 'rgba(255,255,255,0.45)' }}>
              Weekly Clients
            </label>
            <span className="text-lg font-black" style={{ color: GOLD }}>
              {data.weeklyClients >= 100 ? '100+' : data.weeklyClients}
            </span>
          </div>
          <input
            type="range" min={0} max={100} value={data.weeklyClients}
            onChange={(e) => onChange({ weeklyClients: Number(e.target.value) })}
            className="w-full h-2 rounded-full appearance-none cursor-pointer"
            style={{
              background: `linear-gradient(90deg, ${GOLD} ${data.weeklyClients}%, rgba(255,255,255,0.1) ${data.weeklyClients}%)`,
              accentColor: GOLD,
            }}
          />
          <div className="flex justify-between text-[10px] mt-1" style={{ color: 'rgba(255,255,255,0.3)' }}>
            <span>0</span><span>25</span><span>50</span><span>75</span><span>100+</span>
          </div>
        </div>

        {/* Biggest challenges */}
        <div>
          <label className="block text-xs font-semibold mb-3 tracking-wide uppercase" style={{ color: 'rgba(255,255,255,0.45)' }}>
            Biggest Challenges <span style={{ color: 'rgba(255,255,255,0.25)' }}>(select all that apply)</span>
          </label>
          <div className="grid grid-cols-2 gap-2">
            {challenges.map((ch) => (
              <button
                key={ch.value}
                type="button"
                onClick={() => toggleChallenge(ch.value)}
                className="flex items-center gap-3 p-3 rounded-xl text-left transition-all duration-200"
                style={{
                  background: data.biggestChallenges.includes(ch.value) ? `rgba(${GOLD_RGB},0.12)` : 'rgba(255,255,255,0.04)',
                  border: `1.5px solid ${data.biggestChallenges.includes(ch.value) ? GOLD : 'rgba(255,255,255,0.08)'}`,
                }}
              >
                <span className="text-lg">{ch.icon}</span>
                <span className="text-xs" style={{ color: data.biggestChallenges.includes(ch.value) ? GOLD : 'rgba(255,255,255,0.5)' }}>
                  {ch.label}
                </span>
                {data.biggestChallenges.includes(ch.value) && <Check size={12} className="ml-auto" style={{ color: GOLD }} />}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="flex gap-3 mt-8">
        <button type="button" onClick={onBack}
          className="flex items-center gap-2 px-5 py-3 rounded-xl text-sm font-semibold transition-all hover:opacity-80"
          style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.6)' }}
        >
          <ArrowLeft size={16} /> Back
        </button>
        <button type="button" onClick={() => { if (validate()) onNext(); }}
          className="flex-1 py-3 rounded-xl text-sm font-black tracking-wide transition-all duration-200 hover:scale-[1.01] active:scale-[0.99] flex items-center justify-center gap-2"
          style={{ background: `linear-gradient(135deg, ${GOLD}, #a8832a)`, color: NAVY, boxShadow: `0 4px 20px rgba(${GOLD_RGB},0.35)` }}
        >
          Next <ArrowRight size={16} />
        </button>
      </div>
    </div>
  );
}

// ── Screen 2b: Client Demographics ────────────────────────────────────────
function Screen2b({
  data,
  onChange,
  onNext,
  onBack,
}: {
  data: SurveyData;
  onChange: (d: Partial<SurveyData>) => void;
  onNext: () => void;
  onBack: () => void;
}) {
  const ageGroups = ['18–24', '25–34', '35–44', '45–54', '55+'];
  const genderGroups = ['Men', 'Women', 'Non-binary', 'All'];
  const incomeGroups = ['Under $30K', '$30K–$60K', '$60K–$100K', '$100K+'];

  const toggleArr = (field: 'ageGroups' | 'genderGroups' | 'incomeGroups', v: string) => {
    const cur = data[field];
    onChange({ [field]: cur.includes(v) ? cur.filter((c) => c !== v) : [...cur, v] });
  };

  const demographicSections = [
    { field: 'ageGroups' as const, icon: '👶', label: 'Age Groups', items: ageGroups },
    { field: 'genderGroups' as const, icon: '👥', label: 'Gender', items: genderGroups },
    { field: 'incomeGroups' as const, icon: '💰', label: 'Income Range', items: incomeGroups },
  ];

  return (
    <div className="w-full max-w-[480px] mx-auto">
      <ProgressBar pct={53} label="Step 2 of 4 — Your Clients" />
      <div className="mb-8">
        <h2 className="text-2xl font-black text-white mb-1">Who Are Your Clients?</h2>
        <p className="text-sm" style={{ color: 'rgba(255,255,255,0.45)' }}>
          Help us match you with the right leads for your clientele.
        </p>
      </div>

      <div className="space-y-7">
        {demographicSections.map(({ field, icon, label, items }) => (
          <div key={field}>
            <div className="flex items-center gap-2 mb-3">
              <span className="text-lg">{icon}</span>
              <label className="text-xs font-semibold tracking-wide uppercase" style={{ color: 'rgba(255,255,255,0.45)' }}>
                {label}
              </label>
              <span className="text-xs" style={{ color: 'rgba(255,255,255,0.25)' }}>Optional</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {items.map((item) => {
                const isSelected = data[field].includes(item);
                return (
                  <button
                    key={item}
                    type="button"
                    onClick={() => toggleArr(field, item)}
                    className="px-4 py-2 rounded-xl text-sm font-semibold transition-all duration-200 hover:scale-[1.03]"
                    style={{
                      background: isSelected ? `rgba(${GOLD_RGB},0.15)` : 'rgba(255,255,255,0.05)',
                      border: `1.5px solid ${isSelected ? GOLD : 'rgba(255,255,255,0.1)'}`,
                      color: isSelected ? GOLD : 'rgba(255,255,255,0.5)',
                      boxShadow: isSelected ? `0 0 12px rgba(${GOLD_RGB},0.15)` : 'none',
                    }}
                  >
                    {isSelected && <span className="mr-1.5">✓</span>}
                    {item}
                  </button>
                );
              })}
            </div>
          </div>
        ))}

        {/* Insight callout */}
        <div
          className="flex items-start gap-3 px-4 py-4 rounded-2xl"
          style={{ background: `rgba(${GOLD_RGB},0.06)`, border: `1px solid rgba(${GOLD_RGB},0.2)` }}
        >
          <Sparkles size={16} style={{ color: GOLD, flexShrink: 0, marginTop: 2 }} />
          <p className="text-xs leading-relaxed" style={{ color: `rgba(${GOLD_RGB},0.85)` }}>
            <strong>Why this matters:</strong> Barbers who define their target clientele get 2× more relevant leads and higher rebooking rates.
          </p>
        </div>
      </div>

      <div className="flex gap-3 mt-8">
        <button type="button" onClick={onBack}
          className="flex items-center gap-2 px-5 py-3 rounded-xl text-sm font-semibold transition-all hover:opacity-80"
          style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.6)' }}
        >
          <ArrowLeft size={16} /> Back
        </button>
        <button type="button" onClick={onNext}
          className="flex-1 py-3 rounded-xl text-sm font-black tracking-wide transition-all duration-200 hover:scale-[1.01] active:scale-[0.99] flex items-center justify-center gap-2"
          style={{ background: `linear-gradient(135deg, ${GOLD}, #a8832a)`, color: NAVY, boxShadow: `0 4px 20px rgba(${GOLD_RGB},0.35)` }}
        >
          Next <ArrowRight size={16} />
        </button>
      </div>
    </div>
  );
}

// ── Screen 2c: Peak Hours ─────────────────────────────────────────────────
function Screen2c({
  data,
  onChange,
  onNext,
  onBack,
}: {
  data: SurveyData;
  onChange: (d: Partial<SurveyData>) => void;
  onNext: () => void;
  onBack: () => void;
}) {
  const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

  const togglePeakHour = (day: string, slot: string) => {
    const cur = data.peakHours[day] || [];
    onChange({
      peakHours: {
        ...data.peakHours,
        [day]: cur.includes(slot) ? cur.filter((s) => s !== slot) : [...cur, slot],
      },
    });
  };

  return (
    <div className="w-full max-w-[480px] mx-auto">
      <ProgressBar pct={62} label="Step 3 of 4 — Your Schedule" />
      <div className="mb-8">
        <h2 className="text-2xl font-black text-white mb-1">When Are You Busiest?</h2>
        <p className="text-sm" style={{ color: 'rgba(255,255,255,0.45)' }}>
          Mark your peak hours so we can send leads when you're ready to take them.
        </p>
      </div>

      <div className="space-y-7">
        {/* Peak hours */}
        <div>
          <label className="block text-xs font-semibold mb-3 tracking-wide uppercase" style={{ color: 'rgba(255,255,255,0.45)' }}>
            Peak Hours <span style={{ color: 'rgba(255,255,255,0.25)' }}>Optional</span>
          </label>
          <div className="space-y-2">
            {days.map((day) => (
              <div key={day} className="flex items-center gap-3">
                <span className="text-xs font-semibold w-8 shrink-0" style={{ color: 'rgba(255,255,255,0.5)' }}>{day}</span>
                <div className="flex gap-2">
                  {['AM', 'PM'].map((slot) => (
                    <button
                      key={slot}
                      type="button"
                      onClick={() => togglePeakHour(day, slot)}
                      className="px-3 py-1 rounded-lg text-xs font-semibold transition-all"
                      style={{
                        background: (data.peakHours[day] || []).includes(slot) ? `rgba(${GOLD_RGB},0.15)` : 'rgba(255,255,255,0.04)',
                        border: `1px solid ${(data.peakHours[day] || []).includes(slot) ? GOLD : 'rgba(255,255,255,0.08)'}`,
                        color: (data.peakHours[day] || []).includes(slot) ? GOLD : 'rgba(255,255,255,0.4)',
                      }}
                    >
                      {slot}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <p className="text-xs mt-4 leading-relaxed" style={{ color: `rgba(${GOLD_RGB},0.7)` }}>
            💡 We use your peak hours to prioritize lead delivery — so you're never overwhelmed during slow times.
          </p>
        </div>
      </div>

      <div className="flex gap-3 mt-8">
        <button type="button" onClick={onBack}
          className="flex items-center gap-2 px-5 py-3 rounded-xl text-sm font-semibold transition-all hover:opacity-80"
          style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.6)' }}
        >
          <ArrowLeft size={16} /> Back
        </button>
        <button type="button" onClick={onNext}
          className="flex-1 py-3 rounded-xl text-sm font-black tracking-wide transition-all duration-200 hover:scale-[1.01] active:scale-[0.99] flex items-center justify-center gap-2"
          style={{ background: `linear-gradient(135deg, ${GOLD}, #a8832a)`, color: NAVY, boxShadow: `0 4px 20px rgba(${GOLD_RGB},0.35)` }}
        >
          Next <ArrowRight size={16} />
        </button>
      </div>
    </div>
  );
}

// ── Screen 2d: Growth Goal ────────────────────────────────────────────────
function Screen2d({
  data,
  onChange,
  onNext,
  onBack,
}: {
  data: SurveyData;
  onChange: (d: Partial<SurveyData>) => void;
  onNext: () => void;
  onBack: () => void;
}) {
  const [errors, setErrors] = useState<Record<string, string>>({});

  const validate = () => {
    const errs: Record<string, string> = {};
    if (!data.planInterest) errs.planInterest = 'Select a plan interest';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const getMicrocopy = () => {
    if (data.bookingSystem === 'none' || data.bookingSystem === 'manual')
      return '🎉 Get Ahead automates your bookings so you can focus on cutting hair, not chasing clients.';
    if (data.weeklyClients < 10) return '🚀 With our lead system, you could double that in 30 days.';
    if (data.weeklyClients >= 40) return "🔥 You're already busy — let's focus on retention and premium pricing.";
    if (data.biggestChallenges.includes('empty_chairs')) return '🎯 Empty chairs are our specialty. Most barbers fill 15+ slots in week one.';
    if (data.biggestChallenges.includes('marketing')) return '📣 We handle the marketing. You just show up and cut.';
    return null;
  };

  const microcopy = getMicrocopy();

  return (
    <div className="w-full max-w-[480px] mx-auto">
      <ProgressBar pct={75} label="Step 4 of 4 — Your Goal" />
      <div className="mb-8">
        <h2 className="text-2xl font-black text-white mb-1">What's Your Growth Goal?</h2>
        <p className="text-sm" style={{ color: 'rgba(255,255,255,0.45)' }}>
          Tell us what you're most focused on — we'll match you with the right plan.
        </p>
      </div>

      <div className="space-y-7">
        {/* Plan interest */}
        <div>
          <label className="block text-xs font-semibold mb-3 tracking-wide uppercase" style={{ color: 'rgba(255,255,255,0.45)' }}>
            What Are You Most Interested In? *
          </label>
          <div className="grid grid-cols-2 gap-3">
            {[
              { value: 'instant_leads', label: 'Instant Leads', sub: 'Fill chairs fast', icon: '⚡' },
              { value: 'training_leads', label: 'Training + Leads', sub: 'Grow long-term', icon: '📚' },
            ].map((plan) => (
              <button
                key={plan.value}
                type="button"
                onClick={() => onChange({ planInterest: plan.value })}
                className="flex flex-col items-start gap-2 p-4 rounded-2xl transition-all duration-200 hover:scale-[1.02]"
                style={{
                  background: data.planInterest === plan.value ? `rgba(${GOLD_RGB},0.15)` : 'rgba(255,255,255,0.04)',
                  border: `2px solid ${data.planInterest === plan.value ? GOLD : 'rgba(255,255,255,0.08)'}`,
                  boxShadow: data.planInterest === plan.value ? `0 0 20px rgba(${GOLD_RGB},0.2)` : 'none',
                }}
              >
                <span className="text-2xl">{plan.icon}</span>
                <div>
                  <p className="text-sm font-bold text-white">{plan.label}</p>
                  <p className="text-xs" style={{ color: 'rgba(255,255,255,0.4)' }}>{plan.sub}</p>
                </div>
              </button>
            ))}
          </div>
          {errors.planInterest && <p className="text-xs text-red-400 mt-1.5 ml-1">{errors.planInterest}</p>}
        </div>

        {/* Conditional microcopy */}
        {microcopy && (
          <div
            className="px-4 py-3 rounded-xl text-sm leading-relaxed"
            style={{ background: `rgba(${GOLD_RGB},0.08)`, border: `1px solid rgba(${GOLD_RGB},0.2)`, color: 'rgba(255,255,255,0.8)' }}
          >
            {microcopy}
          </div>
        )}

        {/* Insight callout */}
        <div
          className="flex items-start gap-3 px-4 py-4 rounded-2xl"
          style={{ background: `rgba(${GOLD_RGB},0.06)`, border: `1px solid rgba(${GOLD_RGB},0.2)` }}
        >
          <Sparkles size={16} style={{ color: GOLD, flexShrink: 0, marginTop: 2 }} />
          <p className="text-xs leading-relaxed" style={{ color: `rgba(${GOLD_RGB},0.85)` }}>
            <strong>Almost there!</strong> Your personalized plan is generated based on everything you've shared. One click and you'll see exactly what we built for you.
          </p>
        </div>
      </div>

      <div className="flex gap-3 mt-8">
        <button type="button" onClick={onBack}
          className="flex items-center gap-2 px-5 py-3 rounded-xl text-sm font-semibold transition-all hover:opacity-80"
          style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.6)' }}
        >
          <ArrowLeft size={16} /> Back
        </button>
        <button type="button" onClick={() => { if (validate()) onNext(); }}
          className="flex-1 py-3 rounded-xl text-sm font-black tracking-wide transition-all duration-200 hover:scale-[1.01] active:scale-[0.99] flex items-center justify-center gap-2"
          style={{ background: `linear-gradient(135deg, ${GOLD}, #a8832a)`, color: NAVY, boxShadow: `0 4px 20px rgba(${GOLD_RGB},0.35)` }}
        >
          See My Plan <ArrowRight size={16} />
        </button>
      </div>
    </div>
  );
}

// ── Screen 3: Personalized Plan ────────────────────────────────────────────
function Screen3({
  profile,
  survey,
  onNext,
  onBack,
}: {
  profile: ProfileData;
  survey: SurveyData;
  onNext: (plan: string) => void;
  onBack: () => void;
}) {
  const [hoveredPlan, setHoveredPlan] = useState(false);

  const getPlan = () => {
    if (survey.planInterest === 'training_leads' || survey.yearsInBusiness === 'rookie') {
      return {
        name: 'Growth Accelerator',
        tagline: 'Training + Leads — Built for Rising Barbers',
        price: '$49/mo',
        features: [
          '20 pre-booked leads per month',
          'Weekly training modules from master barbers',
          'Client retention playbook',
          'SMS follow-up automation',
          'Priority support',
        ],
        badge: '🎓 Best for New Barbers',
        color: '#4ade80',
      };
    }
    if (survey.weeklyClients >= 30 || survey.yearsInBusiness === 'master') {
      return {
        name: 'Elite Pipeline',
        tagline: 'Maximum Leads — For Established Barbers',
        price: '$99/mo',
        features: [
          '50 pre-booked leads per month',
          'Premium client matching algorithm',
          'Dedicated account manager',
          'Advanced analytics dashboard',
          'White-glove onboarding',
        ],
        badge: '👑 Best for Master Barbers',
        color: GOLD,
      };
    }
    return {
      name: 'Instant Leads',
      tagline: 'Fill Your Chair Fast — No Waiting',
      price: '$29/mo',
      features: [
        '20 pre-booked leads per month',
        'Clients pay $20 deposit upfront',
        'Automated appointment reminders',
        'Basic analytics',
        'Email support',
      ],
      badge: '⚡ Most Popular',
      color: '#60a5fa',
    };
  };

  const plan = getPlan();

  const testimonial = {
    quote: survey.biggestChallenges.includes('empty_chairs')
      ? 'I had 3 empty chairs every day. Week one with this plan — fully booked. I made $600 before noon.' :'The system just works. Clients show up, they\'re pre-committed, and they rebook. My revenue doubled.',
    name: 'Marcus J.',
    shop: "Marcus's Fade House, Atlanta",
  };

  return (
    <div className="w-full max-w-[480px] mx-auto">
      <ProgressBar pct={75} label="Almost There" />

      <div className="mb-8">
        <div
          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-bold mb-4"
          style={{ background: `rgba(${GOLD_RGB},0.12)`, border: `1px solid rgba(${GOLD_RGB},0.3)`, color: GOLD }}
        >
          <Sparkles size={11} />
          Personalized for {profile.fullName || 'You'}
        </div>
        <h2 className="text-2xl font-black text-white mb-1">Your Recommended Plan</h2>
        <p className="text-sm" style={{ color: 'rgba(255,255,255,0.45)' }}>
          Based on your survey, here's what we built for you.
        </p>
      </div>

      {/* Plan card */}
      <div
        className="relative rounded-2xl p-6 mb-6 transition-all duration-300"
        style={{
          background: `rgba(${GOLD_RGB},0.06)`,
          border: `2px solid ${plan.color}`,
          boxShadow: `0 0 40px ${plan.color}22`,
        }}
        onMouseEnter={() => setHoveredPlan(true)}
        onMouseLeave={() => setHoveredPlan(false)}
      >
        {/* Badge */}
        <div
          className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold mb-4"
          style={{ background: `${plan.color}22`, border: `1px solid ${plan.color}55`, color: plan.color }}
        >
          {plan.badge}
        </div>

        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="text-xl font-black text-white">{plan.name}</h3>
            <p className="text-sm mt-0.5" style={{ color: 'rgba(255,255,255,0.5)' }}>{plan.tagline}</p>
          </div>
          <div className="text-right">
            <p className="text-2xl font-black" style={{ color: plan.color }}>{plan.price}</p>
            <p className="text-xs" style={{ color: 'rgba(255,255,255,0.3)' }}>billed monthly</p>
          </div>
        </div>

        <div className="space-y-2.5 mb-5">
          {plan.features.map((f, i) => (
            <div key={i} className="flex items-center gap-3">
              <div
                className="w-5 h-5 rounded-full flex items-center justify-center shrink-0"
                style={{ background: `${plan.color}22` }}
              >
                <Check size={11} style={{ color: plan.color }} strokeWidth={3} />
              </div>
              <span className="text-sm" style={{ color: 'rgba(255,255,255,0.8)' }}>{f}</span>
            </div>
          ))}
        </div>

        {/* Hover tooltip */}
        {hoveredPlan && (
          <div
            className="absolute -top-2 left-1/2 -translate-x-1/2 -translate-y-full w-64 rounded-xl p-3 text-xs leading-relaxed z-10"
            style={{
              background: 'rgba(10,14,26,0.97)',
              border: `1px solid rgba(${GOLD_RGB},0.3)`,
              color: 'rgba(255,255,255,0.85)',
              boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
            }}
          >
            <strong style={{ color: GOLD }}>Why this plan?</strong>
            <br />
            Based on your {survey.weeklyClients} weekly clients, {survey.yearsInBusiness} experience level, and interest in{' '}
            {survey.planInterest === 'instant_leads' ? 'instant leads' : 'training + leads'}.
          </div>
        )}
      </div>

      {/* Social proof */}
      <div
        className="rounded-2xl p-5 mb-6"
        style={{
          background: 'rgba(255,255,255,0.04)',
          border: `1px solid rgba(${GOLD_RGB},0.15)`,
        }}
      >
        <div className="flex gap-0.5 mb-3">
          {[1, 2, 3, 4, 5].map((s) => (
            <Star key={s} size={12} fill={GOLD} style={{ color: GOLD }} />
          ))}
        </div>
        <p className="text-sm leading-relaxed mb-3" style={{ color: 'rgba(255,255,255,0.8)' }}>
          "{testimonial.quote}"
        </p>
        <div className="flex items-center gap-2">
          <div
            className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-black"
            style={{ background: `rgba(${GOLD_RGB},0.2)`, color: GOLD }}
          >
            MJ
          </div>
          <div>
            <p className="text-xs font-bold text-white">{testimonial.name}</p>
            <p className="text-[10px]" style={{ color: 'rgba(255,255,255,0.4)' }}>{testimonial.shop}</p>
          </div>
        </div>
      </div>

      <div className="flex gap-3">
        <button
          type="button"
          onClick={onBack}
          className="flex items-center gap-2 px-5 py-3 rounded-xl text-sm font-semibold transition-all hover:opacity-80"
          style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.6)' }}
        >
          <ArrowLeft size={16} /> Back
        </button>
        <button
          type="button"
          onClick={() => onNext(plan.name)}
          className="flex-1 py-3 rounded-xl text-sm font-black tracking-wide transition-all duration-200 hover:scale-[1.01] active:scale-[0.99] flex items-center justify-center gap-2"
          style={{
            background: `linear-gradient(135deg, ${GOLD}, #a8832a)`,
            color: NAVY,
            boxShadow: `0 4px 20px rgba(${GOLD_RGB},0.35)`,
          }}
        >
          Yes! I Want This Plan <ArrowRight size={16} />
        </button>
      </div>
    </div>
  );
}

// ── Screen 4: Account Security ─────────────────────────────────────────────
function Screen4({
  data,
  onChange,
  onSubmit,
  onBack,
  isLoading,
  error,
}: {
  data: AccountData;
  onChange: (d: Partial<AccountData>) => void;
  onSubmit: () => void;
  onBack: () => void;
  isLoading: boolean;
  error: string;
}) {
  const [errors, setErrors] = useState<Record<string, string>>({});

  const validate = () => {
    const errs: Record<string, string> = {};
    if (!data.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email)) {
      errs.email = 'Enter a valid email address';
    }
    if (data.password.length < 8) errs.password = 'Password must be at least 8 characters';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const passwordStrength = () => {
    const p = data.password;
    if (!p) return 0;
    let score = 0;
    if (p.length >= 8) score++;
    if (/[A-Z]/.test(p)) score++;
    if (/[0-9]/.test(p)) score++;
    if (/[^A-Za-z0-9]/.test(p)) score++;
    return score;
  };

  const strength = passwordStrength();
  const strengthColors = ['', '#ef4444', '#f97316', '#eab308', '#22c55e'];
  const strengthLabels = ['', 'Weak', 'Fair', 'Good', 'Strong'];

  return (
    <div className="w-full max-w-[480px] mx-auto">
      <ProgressBar pct={90} label="Almost Done" />

      <div className="mb-8">
        <div
          className="w-14 h-14 rounded-2xl flex items-center justify-center mb-4"
          style={{ background: `rgba(${GOLD_RGB},0.12)`, border: `1px solid rgba(${GOLD_RGB},0.3)` }}
        >
          <Shield size={24} style={{ color: GOLD }} />
        </div>
        <h2 className="text-2xl font-black text-white mb-1">Secure Your Account</h2>
        <p className="text-sm" style={{ color: 'rgba(255,255,255,0.45)' }}>
          One last step — create your login credentials.
        </p>
      </div>

      <div className="space-y-5">
        <div>
          <label className="block text-xs font-semibold mb-2 tracking-wide uppercase" style={{ color: 'rgba(255,255,255,0.45)' }}>
            Email Address *
          </label>
          <GoldInput
            type="email"
            placeholder="marcus@fadehouse.com"
            value={data.email}
            onChange={(v) => onChange({ email: v })}
            error={errors.email}
          />
        </div>

        <div>
          <label className="block text-xs font-semibold mb-2 tracking-wide uppercase" style={{ color: 'rgba(255,255,255,0.45)' }}>
            Password *
          </label>
          <GoldInput
            placeholder="Create a strong password"
            value={data.password}
            onChange={(v) => onChange({ password: v })}
            error={errors.password}
            showToggle
            onToggle={() => onChange({ showPassword: !data.showPassword })}
            show={data.showPassword}
          />
          {data.password && (
            <div className="mt-2">
              <div className="flex gap-1 mb-1">
                {[1, 2, 3, 4].map((i) => (
                  <div
                    key={i}
                    className="flex-1 h-1 rounded-full transition-all duration-300"
                    style={{ background: i <= strength ? strengthColors[strength] : 'rgba(255,255,255,0.08)' }}
                  />
                ))}
              </div>
              <p className="text-xs" style={{ color: strengthColors[strength] || 'rgba(255,255,255,0.3)' }}>
                {strengthLabels[strength]}
              </p>
            </div>
          )}
        </div>

        {/* Security info */}
        <div
          className="flex items-start gap-3 px-4 py-3 rounded-xl"
          style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}
        >
          <Lock size={14} className="mt-0.5 shrink-0" style={{ color: `rgba(${GOLD_RGB},0.7)` }} />
          <p className="text-xs leading-relaxed" style={{ color: 'rgba(255,255,255,0.5)' }}>
            Your account is protected with industry-standard encryption. We never share your data.
          </p>
        </div>

        {error && (
          <div
            className="px-4 py-3 rounded-xl text-sm"
            style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', color: '#fca5a5' }}
          >
            {error}
          </div>
        )}
      </div>

      <div className="flex gap-3 mt-8">
        <button
          type="button"
          onClick={onBack}
          disabled={isLoading}
          className="flex items-center gap-2 px-5 py-3 rounded-xl text-sm font-semibold transition-all hover:opacity-80"
          style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.6)' }}
        >
          <ArrowLeft size={16} /> Back
        </button>
        <button
          type="button"
          onClick={() => { if (validate()) onSubmit(); }}
          disabled={isLoading}
          className="flex-1 py-3 rounded-xl text-sm font-black tracking-wide transition-all duration-200 hover:scale-[1.01] active:scale-[0.99] flex items-center justify-center gap-2"
          style={{
            background: `linear-gradient(135deg, ${GOLD}, #a8832a)`,
            color: NAVY,
            boxShadow: `0 4px 20px rgba(${GOLD_RGB},0.35)`,
            opacity: isLoading ? 0.7 : 1,
          }}
        >
          {isLoading ? (
            <><Loader2 size={16} className="animate-spin" /> Creating Account...</>
          ) : (
            <><Shield size={16} /> Create My Account</>
          )}
        </button>
      </div>
    </div>
  );
}
// ── Screen 5: Dashboard Orientation ───────────────────────────────────────
function Screen5({
  profile,
  plan,
  onGoToDashboard,
}: {
  profile: ProfileData;
  plan: string;
  onGoToDashboard: () => void;
}) {
  const [showBadge, setShowBadge] = useState(false);
  const [tooltipVisible, setTooltipVisible] = useState<string | null>(null);

  useEffect(() => {
    const t = setTimeout(() => setShowBadge(true), 800);
    return () => clearTimeout(t);
  }, []);

  const panels = [
    {
      id: 'leads',
      icon: <Users size={20} style={{ color: '#60a5fa' }} />,
      title: 'Leads',
      value: '0',
      sub: 'New leads incoming',
      color: '#60a5fa',
      tooltip: 'Your leads dashboard shows all incoming client requests. Each lead is pre-qualified and ready to come in.',
      locked: false,
    },
    {
      id: 'training',
      icon: <BookOpen size={20} style={{ color: GOLD }} />,
      title: 'Tips & Training',
      value: '12',
      sub: 'Modules available',
      color: GOLD,
      tooltip: 'Access expert training from master barbers. New modules drop every week.',
      locked: false,
    },
  ];

  return (
    <div className="w-full max-w-[480px] mx-auto">
      <ProgressBar pct={100} label="Complete!" />

      {/* Account Live badge */}
      <div className="text-center mb-8">
        <div
          className="inline-flex flex-col items-center gap-3 px-8 py-6 rounded-2xl mb-4 transition-all duration-700"
          style={{
            background: `rgba(${GOLD_RGB},0.1)`,
            border: `2px solid ${GOLD}`,
            boxShadow: showBadge ? `0 0 40px rgba(${GOLD_RGB},0.3), 0 0 80px rgba(${GOLD_RGB},0.1)` : 'none',
            transform: showBadge ? 'scale(1)' : 'scale(0.8)',
            opacity: showBadge ? 1 : 0,
          }}
        >
          <div
            className="w-16 h-16 rounded-full flex items-center justify-center"
            style={{ background: `rgba(${GOLD_RGB},0.2)`, border: `2px solid ${GOLD}` }}
          >
            <CheckCircle2 size={32} style={{ color: GOLD }} />
          </div>
          <div>
            <p className="text-xl font-black text-white">Account Live! 🎉</p>
            <p className="text-sm mt-1" style={{ color: `rgba(${GOLD_RGB},0.8)` }}>
              Welcome to The Barber Sphere, {profile.fullName?.split(' ')[0] || 'Barber'}
            </p>
          </div>
          <div
            className="px-4 py-1.5 rounded-full text-xs font-bold"
            style={{ background: `rgba(${GOLD_RGB},0.2)`, color: GOLD }}
          >
            {plan} Plan Activated
          </div>
        </div>

        <h2 className="text-xl font-black text-white mb-1">Your Dashboard is Ready</h2>
        <p className="text-sm" style={{ color: 'rgba(255,255,255,0.45)' }}>
          Here's a preview of what's waiting for you.
        </p>
      </div>

      {/* Dashboard panels */}
      <div className="space-y-3 mb-6">
        {panels.map((panel) => (
          <div
            key={panel.id}
            className="relative rounded-2xl p-4 cursor-pointer transition-all duration-200 hover:scale-[1.01]"
            style={{
              background: 'rgba(255,255,255,0.04)',
              border: `1px solid rgba(255,255,255,0.08)`,
            }}
            onMouseEnter={() => setTooltipVisible(panel.id)}
            onMouseLeave={() => setTooltipVisible(null)}
          >
            <div className="flex items-center gap-4">
              <div
                className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0"
                style={{ background: `${panel.color}15`, border: `1px solid ${panel.color}30` }}
              >
                {panel.icon}
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-bold text-white">{panel.title}</p>
                </div>
                <p className="text-xs mt-0.5" style={{ color: 'rgba(255,255,255,0.4)' }}>{panel.sub}</p>
              </div>
              <div className="text-right">
                <p className="text-2xl font-black" style={{ color: panel.color }}>{panel.value}</p>
              </div>
            </div>

            {/* Tooltip hint */}
            {tooltipVisible === panel.id && (
              <div
                className="absolute left-4 right-4 bottom-full mb-2 rounded-xl p-3 text-xs leading-relaxed z-10"
                style={{
                  background: 'rgba(10,14,26,0.97)',
                  border: `1px solid rgba(${GOLD_RGB},0.3)`,
                  color: 'rgba(255,255,255,0.85)',
                  boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
                }}
              >
                💡 {panel.tooltip}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Quick wins */}
      <div
        className="rounded-2xl p-5 mb-6"
        style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}
      >
        <p className="text-xs font-bold mb-3 tracking-wide uppercase" style={{ color: 'rgba(255,255,255,0.4)' }}>
          Your First 24 Hours
        </p>
        <div className="space-y-2.5">
          {[
            { icon: '✅', text: 'Profile created and verified' },
            { icon: '🎯', text: 'Plan activated — leads matching now' },
            { icon: '📱', text: 'Check your email for onboarding guide' },
            { icon: '📚', text: 'Start with Module 1: Client Psychology' },
          ].map((item, i) => (
            <div key={i} className="flex items-center gap-3">
              <span className="text-base">{item.icon}</span>
              <span className="text-sm" style={{ color: 'rgba(255,255,255,0.7)' }}>{item.text}</span>
            </div>
          ))}
        </div>
      </div>

      <button
        type="button"
        onClick={onGoToDashboard}
        className="w-full py-4 rounded-2xl text-base font-black tracking-wide transition-all duration-200 hover:scale-[1.02] active:scale-[0.98] flex items-center justify-center gap-3"
        style={{
          background: `linear-gradient(135deg, ${GOLD}, #a8832a)`,
          color: NAVY,
          boxShadow: `0 8px 32px rgba(${GOLD_RGB},0.4)`,
        }}
      >
        <TrendingUp size={18} />
        Go to My Dashboard
        <ArrowRight size={18} />
      </button>
    </div>
  );
}

// ── Main Page ──────────────────────────────────────────────────────────────
export default function BarberSignupPage() {
  const router = useRouter();
  const [screen, setScreen] = useState(0);
  const [confettiActive, setConfettiActive] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [selectedPlan, setSelectedPlan] = useState('');

  const [profile, setProfile] = useState<ProfileData>({
    fullName: '',
    shopName: '',
    location: '',
    locationConfirmed: false,
    instagram: '',
    tiktok: '',
    facebook: '',
    yearsInBusiness: '',
    profilePic: null,
    profilePicUrl: '',
  });

  const [survey, setSurvey] = useState<SurveyData>({
    bookingSystem: '',
    bookingSystemOther: '',
    weeklyClients: 10,
    biggestChallenges: [],
    ageGroups: [],
    genderGroups: [],
    incomeGroups: [],
    peakHours: {},
    planInterest: '',
  });

  const [account, setAccount] = useState<AccountData>({
    email: '',
    password: '',
    showPassword: false,
  });

  const handleSubmit = useCallback(async () => {
    setIsLoading(true);
    setSubmitError('');

    try {
      // Create auth account
      const authRes = await fetch('/api/create-auth-user', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: account.email.trim().toLowerCase(),
          password: account.password,
          metadata: {
            full_name: profile.fullName,
            shop_name: profile.shopName,
            role: 'barber',
          },
        }),
      });

      const authData = await authRes.json();

      if (!authRes.ok && authData.code === 'user_already_exists') {
        setSubmitError('This email is already registered. Please sign in instead.');
        setIsLoading(false);
        return;
      }

      if (!authRes.ok || !authData.success) {
        setSubmitError(authData.message || 'Failed to create account. Please try again.');
        setIsLoading(false);
        return;
      }

      // Save onboarding data to Supabase
      const supabase = createClient();
      const { error: dbError } = await supabase.from('barber_onboarding').insert({
        auth_user_id: authData.userId || null,
        full_name: profile.fullName,
        shop_name: profile.shopName,
        location: profile.location,
        social_links: {
          instagram: profile.instagram,
          tiktok: profile.tiktok,
          facebook: profile.facebook,
        },
        years_in_business: profile.yearsInBusiness,
        booking_system: survey.bookingSystem,
        booking_system_other: survey.bookingSystemOther,
        weekly_clients: survey.weeklyClients,
        biggest_challenges: survey.biggestChallenges,
        client_demographics: {
          age_groups: survey.ageGroups,
          gender_groups: survey.genderGroups,
          income_groups: survey.incomeGroups,
        },
        peak_hours: survey.peakHours,
        plan_interest: survey.planInterest,
        selected_plan: selectedPlan,
        email: account.email.trim().toLowerCase(),
        status: 'active',
      });

      if (dbError) {
        console.warn('[barber-signup] Onboarding data save failed:', dbError.message);
      }

      // Also save to barber_signups for backwards compatibility
      await fetch('/api/submit-barber-signup', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          fullName: profile.fullName,
          shopName: profile.shopName,
          phone: '',
          city: profile.location,
          email: account.email.trim().toLowerCase(),
          bookingSystem: survey.bookingSystem,
          avgCutPrice: '',
          appointmentsPerWeek: String(survey.weeklyClients),
          yearsExperience: profile.yearsInBusiness,
        }),
      }).catch(() => {});

      // Trigger confetti and advance to screen 5
      setConfettiActive(true);
      setTimeout(() => setConfettiActive(false), 4000);
      setScreen(8);
    } catch (err) {
      console.error('[barber-signup] Error:', err);
      setSubmitError('Something went wrong. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [account, profile, survey, selectedPlan]);

  const scrollToTop = () => {
    if (typeof window !== 'undefined') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const goToScreen = (n: number) => {
    setScreen(n);
    scrollToTop();
  };

  // Screen labels for step indicator
  const screenLabels = ['Welcome', 'Profile', 'Shop', 'Clients', 'Schedule', 'Goal', 'Plan', 'Security', 'Dashboard'];

  return (
    <div
      className="min-h-screen"
      style={{ background: `linear-gradient(180deg, ${NAVY} 0%, ${DARK} 100%)`, fontFamily: "'DM Sans', sans-serif" }}
    >
      <Confetti active={confettiActive} />

      {/* Top nav */}
      {screen > 0 && screen < 8 && (
        <div
          className="sticky top-0 z-40 flex items-center justify-between px-6 py-4"
          style={{
            background: 'rgba(10,14,26,0.9)',
            backdropFilter: 'blur(12px)',
            borderBottom: '1px solid rgba(255,255,255,0.06)',
          }}
        >
          {/* Logo */}
          <div className="flex items-center gap-2">
            <div
              className="w-8 h-8 rounded-lg flex items-center justify-center"
              style={{ background: `rgba(${GOLD_RGB},0.15)`, border: `1px solid rgba(${GOLD_RGB},0.3)` }}
            >
              <Scissors size={14} style={{ color: GOLD }} />
            </div>
            <span className="text-sm font-black text-white hidden sm:block">The Barber Sphere</span>
          </div>

          {/* Step dots — 7 steps: Profile, Shop, Clients, Schedule, Goal, Plan, Security */}
          <div className="flex items-center gap-1.5">
            {[1, 2, 3, 4, 5, 6, 7].map((s) => (
              <div
                key={s}
                className="rounded-full transition-all duration-300"
                style={{
                  width: screen === s ? '20px' : '6px',
                  height: '6px',
                  background: screen >= s ? GOLD : 'rgba(255,255,255,0.15)',
                }}
              />
            ))}
          </div>

          <button
            onClick={() => router.push('/barber-login')}
            className="text-xs font-medium transition-opacity hover:opacity-80"
            style={{ color: 'rgba(255,255,255,0.4)' }}
          >
            Sign in <span style={{ color: GOLD }}>→</span>
          </button>
        </div>
      )}

      {/* Main content */}
      <div className={screen === 0 ? '' : 'px-6 py-10 flex justify-center'}>
        {screen === 0 && (
          <Screen0 onNext={() => goToScreen(1)} />
        )}
        {screen === 1 && (
          <Screen1
            data={profile}
            onChange={(d) => setProfile((p) => ({ ...p, ...d }))}
            onNext={() => goToScreen(2)}
            onBack={() => goToScreen(0)}
          />
        )}
        {screen === 2 && (
          <Screen2a
            data={survey}
            onChange={(d) => setSurvey((s) => ({ ...s, ...d }))}
            onNext={() => goToScreen(3)}
            onBack={() => goToScreen(1)}
          />
        )}
        {screen === 3 && (
          <Screen2b
            data={survey}
            onChange={(d) => setSurvey((s) => ({ ...s, ...d }))}
            onNext={() => goToScreen(4)}
            onBack={() => goToScreen(2)}
          />
        )}
        {screen === 4 && (
          <Screen2c
            data={survey}
            onChange={(d) => setSurvey((s) => ({ ...s, ...d }))}
            onNext={() => goToScreen(5)}
            onBack={() => goToScreen(3)}
          />
        )}
        {screen === 5 && (
          <Screen2d
            data={survey}
            onChange={(d) => setSurvey((s) => ({ ...s, ...d }))}
            onNext={() => goToScreen(6)}
            onBack={() => goToScreen(4)}
          />
        )}
        {screen === 6 && (
          <Screen3
            profile={profile}
            survey={survey}
            onNext={(plan) => {
              setSelectedPlan(plan);
              goToScreen(7);
            }}
            onBack={() => goToScreen(5)}
          />
        )}
        {screen === 7 && (
          <Screen4
            data={account}
            onChange={(d) => setAccount((a) => ({ ...a, ...d }))}
            onSubmit={handleSubmit}
            onBack={() => goToScreen(6)}
            isLoading={isLoading}
            error={submitError}
          />
        )}
        {screen === 8 && (
          <Screen5
            profile={profile}
            plan={selectedPlan}
            onGoToDashboard={() => router.push('/barber-dashboard')}
          />
        )}
      </div>
    </div>
  );
}
